export const MAP_MARKER_HEIGHT = 28;
export const DEFAULT_MAP_MARKER_WIDTH = 60;
export const LIKED_MAP_MARKER_WIDTH = 76;

const IMAGE_PREFIX = 'marketplace-map-marker|';
const IMAGE_PADDING = 8;
const IMAGE_PIXEL_RATIO = 2;

const CONTENT_PRIMARY = '#23201d';
const CONTENT_INVERSE_PRIMARY = '#fff';
const CONTENT_ERROR = '#c13e3e';
const BACKGROUND_PRIMARY = '#fff';
const BACKGROUND_INVERSE_PRIMARY = '#23201d';
const HEART_PATH =
  'M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.176 -10.215z';

export type MapMarkerImageConfig = {
  /** Whether to draw the keyboard focus indicator. */
  hasFocusRing: boolean;
  /** Whether to use the active foreground and background colors. */
  isHighlighted: boolean;
  /** Whether to reserve space for the favorite heart. */
  isLiked: boolean;
  /** Whether the label represents a pending details request. */
  isLoading: boolean;
  /** Compacted price or loading text drawn inside the pill. */
  label: string;
};

type MarkerImageMap = {
  addImage: (id: string, image: ImageData, options: { pixelRatio: number }) => unknown;
  hasImage: (id: string) => boolean;
  removeImage: (id: string) => unknown;
  updateImage?: (id: string, image: ImageData) => unknown;
};

let cachedMarkerTextStyle: { font: string; letterSpacing: string } | undefined;
const registeredMarkerImageIds = new WeakMap<object, Set<string>>();

export function mapMarkerImageId(config: MapMarkerImageConfig) {
  return `${IMAGE_PREFIX}${Number(config.hasFocusRing)}|${Number(config.isHighlighted)}|${Number(
    config.isLiked,
  )}|${Number(config.isLoading)}|${encodeURIComponent(config.label)}`;
}

type RoundedRectangleOptions = {
  /** Canvas receiving the rounded path. */
  context: CanvasRenderingContext2D;
  /** Rectangle height in CSS pixels. */
  height: number;
  /** Corner radius in CSS pixels. */
  radius: number;
  /** Rectangle width in CSS pixels. */
  width: number;
  /** Horizontal origin in CSS pixels. */
  x: number;
  /** Vertical origin in CSS pixels. */
  y: number;
};

function roundedRectangle({ context, height, radius, width, x, y }: RoundedRectangleOptions) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.arcTo(x + width, y, x + width, y + radius, radius);
  context.lineTo(x + width, y + height - radius);
  context.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  context.lineTo(x + radius, y + height);
  context.arcTo(x, y + height, x, y + height - radius, radius);
  context.lineTo(x, y + radius);
  context.arcTo(x, y, x + radius, y, radius);
  context.closePath();
}

function parseMapMarkerImageId(id: string): MapMarkerImageConfig | null {
  if (!id.startsWith(IMAGE_PREFIX)) {
    return null;
  }
  const [focused, highlighted, liked, loading, encodedLabel] = id
    .slice(IMAGE_PREFIX.length)
    .split('|');
  if (
    (focused !== '0' && focused !== '1') ||
    (highlighted !== '0' && highlighted !== '1') ||
    (liked !== '0' && liked !== '1') ||
    (loading !== '0' && loading !== '1') ||
    encodedLabel == null
  ) {
    return null;
  }
  try {
    return {
      hasFocusRing: focused === '1',
      isHighlighted: highlighted === '1',
      isLiked: liked === '1',
      isLoading: loading === '1',
      label: decodeURIComponent(encodedLabel),
    };
  } catch {
    return null;
  }
}

type DrawHeartOptions = {
  /** Canvas receiving the heart. */
  context: CanvasRenderingContext2D;
  /** Horizontal origin in CSS pixels. */
  x: number;
  /** Vertical origin in CSS pixels. */
  y: number;
};

function drawHeart({ context, x, y }: DrawHeartOptions) {
  const path = new Path2D(HEART_PATH);
  context.save();
  context.translate(x, y);
  context.scale(14 / 24, 14 / 24);
  // oxlint-disable-next-line unicorn/no-array-fill-with-reference-type -- Canvas fill is not Array.fill.
  context.fill(path);
  context.restore();
}

function markerTextStyle() {
  if (cachedMarkerTextStyle) {
    return cachedMarkerTextStyle;
  }
  const probe = document.createElement('span');
  probe.className = 'text-label-sm';
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  document.body.appendChild(probe);
  const computed = window.getComputedStyle(probe);
  const font = `${computed.fontWeight || '500'} ${computed.fontSize || '14px'} ${computed.fontFamily || 'sans-serif'}`;
  const letterSpacing = computed.letterSpacing || '-0.196px';
  probe.remove();
  cachedMarkerTextStyle = { font, letterSpacing };
  return cachedMarkerTextStyle;
}

function createMapMarkerImage(config: MapMarkerImageConfig) {
  const width = config.isLiked ? LIKED_MAP_MARKER_WIDTH : DEFAULT_MAP_MARKER_WIDTH;
  const canvas = document.createElement('canvas');
  canvas.width = (width + IMAGE_PADDING * 2) * IMAGE_PIXEL_RATIO;
  canvas.height = (MAP_MARKER_HEIGHT + IMAGE_PADDING * 2) * IMAGE_PIXEL_RATIO;
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }

  context.scale(IMAGE_PIXEL_RATIO, IMAGE_PIXEL_RATIO);
  const backgroundColor = config.isHighlighted ? BACKGROUND_INVERSE_PRIMARY : BACKGROUND_PRIMARY;

  roundedRectangle({
    context,
    height: MAP_MARKER_HEIGHT,
    radius: MAP_MARKER_HEIGHT / 2,
    width,
    x: IMAGE_PADDING,
    y: IMAGE_PADDING,
  });
  context.fillStyle = backgroundColor;
  // Canvas shadows are softened again when Mapbox downsamples the texture.
  context.shadowColor = 'rgba(0, 0, 0, 0.32)';
  context.shadowBlur = 7;
  context.shadowOffsetY = 2;
  context.fill();

  context.shadowColor = 'transparent';
  roundedRectangle({
    context,
    height: MAP_MARKER_HEIGHT + 2,
    radius: MAP_MARKER_HEIGHT / 2 + 1,
    width: width + 2,
    x: IMAGE_PADDING - 1,
    y: IMAGE_PADDING - 1,
  });
  context.fillStyle = 'rgba(0, 0, 0, 0.08)';
  context.fill();

  roundedRectangle({
    context,
    height: MAP_MARKER_HEIGHT,
    radius: MAP_MARKER_HEIGHT / 2,
    width,
    x: IMAGE_PADDING,
    y: IMAGE_PADDING,
  });
  context.fillStyle = backgroundColor;
  context.fill();

  if (config.hasFocusRing) {
    roundedRectangle({
      context,
      height: MAP_MARKER_HEIGHT + 4,
      radius: MAP_MARKER_HEIGHT / 2 + 2,
      width: width + 4,
      x: IMAGE_PADDING - 2,
      y: IMAGE_PADDING - 2,
    });
    context.lineWidth = 2;
    context.strokeStyle = '#005fcc';
    context.stroke();
  }

  const textStyle = markerTextStyle();
  context.font = textStyle.font;
  context.letterSpacing = textStyle.letterSpacing;
  context.textBaseline = 'middle';
  context.fillStyle = config.isHighlighted ? CONTENT_INVERSE_PRIMARY : CONTENT_PRIMARY;

  const centerY = IMAGE_PADDING + MAP_MARKER_HEIGHT / 2 + 0.5;
  if (config.isLiked && !config.isLoading) {
    const heartSize = 14;
    const gap = 4;
    const textWidth = context.measureText(config.label).width;
    const contentWidth = heartSize + gap + textWidth;
    const contentX = IMAGE_PADDING + (width - contentWidth) / 2;
    context.fillStyle = CONTENT_ERROR;
    drawHeart({ context, x: contentX, y: centerY - heartSize / 2 });
    context.fillStyle = config.isHighlighted ? CONTENT_INVERSE_PRIMARY : CONTENT_PRIMARY;
    context.textAlign = 'left';
    context.fillText(config.label, contentX + heartSize + gap, centerY);
  } else {
    context.textAlign = 'center';
    context.fillText(config.label, IMAGE_PADDING + width / 2, centerY);
  }

  return context.getImageData(0, 0, canvas.width, canvas.height);
}

export function getRegisteredMapMarkerImageIds(map: MarkerImageMap) {
  let imageIds = registeredMarkerImageIds.get(map);
  if (!imageIds) {
    imageIds = new Set();
    registeredMarkerImageIds.set(map, imageIds);
  }
  return imageIds;
}

type MarkerImagesOptions = {
  /** Marker image IDs to register or refresh. */
  imageIds: Iterable<string>;
  /** Mapbox style image registry. */
  map: MarkerImageMap;
};

export function refreshMapMarkerImages({ imageIds, map }: MarkerImagesOptions) {
  cachedMarkerTextStyle = undefined;
  for (const id of imageIds) {
    const imageConfig = parseMapMarkerImageId(id);
    const image = imageConfig ? createMapMarkerImage(imageConfig) : null;
    if (image && map.hasImage(id)) {
      map.updateImage?.(id, image);
    }
  }
}

type RegisterMapMarkerImageOptions = {
  /** Generated marker image ID. */
  id: string;
  /** Mapbox style image registry. */
  map: MarkerImageMap;
};

export function registerMapMarkerImage({ id, map }: RegisterMapMarkerImageOptions) {
  if (map.hasImage(id)) {
    getRegisteredMapMarkerImageIds(map).add(id);
    return;
  }
  const imageConfig = parseMapMarkerImageId(id);
  if (!imageConfig) {
    return;
  }
  const image = createMapMarkerImage(imageConfig);
  if (image) {
    map.addImage(id, image, { pixelRatio: IMAGE_PIXEL_RATIO });
    getRegisteredMapMarkerImageIds(map).add(id);
  }
}

export function registerMapMarkerImages({ imageIds, map }: MarkerImagesOptions) {
  for (const id of imageIds) {
    registerMapMarkerImage({ id, map });
  }
}
